import { MOCK_AUTH } from './fixtures/ids';

const MOCK_COOKIE = 'ttt_mock_mode';
const MOCK_COOKIE_VALUE = '1';
const COOKIE_MAX_AGE_DAYS = 7;

export type MockSignInDefaults = {
  email: string;
  password: string;
};

let workerStop: (() => void | Promise<void>) | null = null;

export function isMockingEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_MOCKING === 'true';
}

function readCookie(): boolean {
  return document.cookie.split(';').some(c => c.trim().startsWith(`${MOCK_COOKIE}=${MOCK_COOKIE_VALUE}`));
}

function setCookie(): void {
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${MOCK_COOKIE}=${MOCK_COOKIE_VALUE}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function clearCookie(): void {
  document.cookie = `${MOCK_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
}

function activateFromQuery(): void {
  const params = new URLSearchParams(window.location.search);
  const mockParam = params.get('mock');
  if (mockParam !== '1' && mockParam !== 'true') return;

  setCookie();
  params.delete('mock');
  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', nextUrl);
}

export function isMockModeActive(): boolean {
  return isMockingEnabled() && readCookie();
}

/** Prefill for the sign-in form when mock mode is active (regular user demo account). */
export function getMockSignInDefaults(): MockSignInDefaults | undefined {
  if (!isMockModeActive()) {
    return undefined;
  }
  return {
    email: MOCK_AUTH.REGULAR_EMAIL,
    password: MOCK_AUTH.PASSWORD,
  };
}

export async function initMockMode(): Promise<void> {
  if (!isMockingEnabled()) return;

  activateFromQuery();
  if (!readCookie()) return;

  const { seedMockDb } = await import('./mockDb');
  const { seedMockAuth } = await import('./mockAuth');
  const { worker } = await import('./browser');

  seedMockDb();
  seedMockAuth();

  await worker.start({ onUnhandledRequest: 'bypass', quiet: true });
  workerStop = () => worker.stop();
}

export async function exitMockMode(): Promise<void> {
  clearCookie();
  const { clearMockAuth } = await import('./mockAuth');
  clearMockAuth();
  if (workerStop) {
    await workerStop();
    workerStop = null;
  }
  window.location.reload();
}
